import { useCalculateMeetingBillableTime, useSaveBillableTimeReason } from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Button from '@components/V4/Button';
import TextArea from '@components/V4/Inputs/Textarea';
import Modal from '@components/V4/Modal/Modal';
import { zodResolver } from '@hookform/resolvers/zod';
import { DISCORD_MENTOR_CHANNEL, fiftyMinutesInSeconds } from '@shared/common';
import {
  addBillableTimeReasonValidation,
  addBillableTimeReasonValidationFormData,
} from '@shared/validations';
import { convertSecondsToTime, mergeClassNames } from 'apps/agora/src/utils/helpers';
import { useForm } from 'react-hook-form';

interface BillableTimePageProps {
  meetingId: string;
  shouldCalculateBillableTime: boolean;
  onClose: () => void;
}

const BillableTimePage = (props: BillableTimePageProps) => {
  const { meetingId, shouldCalculateBillableTime, onClose } = props;

  const { data, isLoading: isBillableTimeProcessing } = useCalculateMeetingBillableTime(meetingId, {
    enabled: shouldCalculateBillableTime,
  });

  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors, touchedFields },
  } = useForm<addBillableTimeReasonValidationFormData>({
    resolver: zodResolver(addBillableTimeReasonValidation),
    reValidateMode: 'onBlur',
    mode: 'onBlur',
  });

  const { mutate: saveUserProfile } = useSaveBillableTimeReason(meetingId, {
    onSuccess: () => {
      onClose();
    },
  });

  const onSubmit = async (data: addBillableTimeReasonValidationFormData) => {
    const isValid = await trigger();
    if (isValid && data.billableTimeReason) {
      saveUserProfile({ billableTimeReason: data.billableTimeReason });
    } else {
      onClose();
    }
  };

  const submitHandler = (e: React.MouseEvent<HTMLButtonElement>) => handleSubmit(onSubmit)(e);

  return isBillableTimeProcessing ? (
    <LoadingSpinner />
  ) : (
    <Modal.Body className="tablet:px-0 gap-6 h-full">
      {shouldCalculateBillableTime ? (
        <>
          {data?.billableTime && data.billableTime !== null ? (
            <>
              <div className="flex flex-col gap-2 tablet:gap-0 tablet:flex-row tablet:justify-between tablet:items-center">
                <h3 className="text-customGrey text-2xl font-semibold">Billable Time</h3>
              </div>

              <div className="flex flex-col gap-3">
                <p>
                  The calculated billable time is:{' '}
                  <span
                    className={mergeClassNames('font-bold', {
                      'text-customPrimary': data.billableTime >= fiftyMinutesInSeconds,
                      'text-customRedAccent': data.billableTime < fiftyMinutesInSeconds,
                    })}
                  >
                    {data?.billableTime === 0 ? '0m' : convertSecondsToTime(data?.billableTime)}
                  </span>
                </p>

                {data?.billableTime < fiftyMinutesInSeconds && (
                  <div className="flex flex-col gap-2">
                    <p>
                      Since the calculated billable time is less than 50 minutes, this session does
                      not qualify as completed.
                    </p>

                    <TextArea
                      id={'billableTimeReason'}
                      label={'Reason'}
                      isTouched={touchedFields.billableTimeReason}
                      isValid={!errors.billableTimeReason}
                      errorText={errors.billableTimeReason?.message}
                      {...register('billableTimeReason')}
                    />

                    <p>
                      If you believe the calculated billable time is incorrect, please reach out to
                      us{' '}
                      <a href={DISCORD_MENTOR_CHANNEL} target="_blank" rel="noreferrer">
                        #
                        <span role="img" aria-label="icon depicting a computer">
                          💻
                        </span>
                        agora-support
                      </a>
                      .
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2 tablet:gap-0 tablet:flex-row tablet:justify-between tablet:items-center">
                <h3 className="text-customGrey text-2xl font-semibold">
                  There was a problem calculating the billable time.
                </h3>
              </div>

              <p>
                We're sorry about the inconvenience, please reach out on{' '}
                <a href={DISCORD_MENTOR_CHANNEL} target="_blank" rel="noreferrer">
                  #
                  <span role="img" aria-label="icon depicting a computer">
                    💻
                  </span>
                  agora-support
                </a>{' '}
                for assistance.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-4">
            <Button
              className="w-24"
              size="large"
              buttonText={
                data?.billableTime && data?.billableTime < fiftyMinutesInSeconds
                  ? 'Send Reason'
                  : 'Close'
              }
              onClick={
                data?.billableTime && data?.billableTime < fiftyMinutesInSeconds
                  ? submitHandler
                  : () => onClose()
              }
            />
          </div>
        </>
      ) : (
        <p>Please submit the feedback first!</p>
      )}
    </Modal.Body>
  );
};

export default BillableTimePage;
